import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import ArrowBackIcon from '@/material-icons/400-24px/arrow_back.svg?react';
import CardGiftcardIcon from '@/material-icons/400-24px/card_giftcard.svg?react';
import CloseIcon from '@/material-icons/400-24px/close.svg?react';
import {
  fetchGiftSubCatalogue,
  startGiftSubCheckout,
} from 'mastodon/actions/rooms';
import { Icon } from 'mastodon/components/icon';
import type {
  GiftSubCatalogue,
  GiftSubPackage,
  GiftSubProvider,
} from 'mastodon/features/rooms/gift_subs';
import {
  assetLabel,
  availableProviders,
  cryptoAssets,
  formatPrice,
  isUsableCatalogue,
  needsProviderChoice,
  packageLabel,
  PROVIDER_HINTS,
  PROVIDER_LABELS,
} from 'mastodon/features/rooms/gift_subs';

interface Props {
  roomId: string;
  hostName: string;
  onClose: () => void;
}

type Step = 'packages' | 'method' | 'asset';

const errorMessageOf = (err: unknown): string => {
  const response = (
    err as { response?: { status?: number; data?: { error?: string } } }
  ).response;

  if (response?.status === 429)
    return 'You have opened a lot of checkouts just now — wait a moment and try again.';

  return response?.data?.error ?? 'Could not start checkout. Please try again.';
};

/**
 * Ticket 73 — the Gift Subs menu.
 *
 * Three steps: pick a package, pick how to pay, and (for crypto) pick which
 * coin. The payment step is skipped entirely when only one rail is available,
 * because a question with one possible answer is just a click.
 *
 * Every price on screen is one the SERVER quoted for this room, fetched when
 * the menu opens. Nothing is hardcoded here on purpose: a client that carries
 * its own price list will eventually show a number that is not what the
 * processor bills, and the first anyone hears about it is a complaint.
 */
export const GiftSubsModal: React.FC<Props> = ({
  roomId,
  hostName,
  onClose,
}) => {
  const [catalogue, setCatalogue] = useState<GiftSubCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>('packages');
  const [chosen, setChosen] = useState<GiftSubPackage | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const request = { live: true };

    void (async () => {
      try {
        const data = await fetchGiftSubCatalogue(roomId);
        if (request.live) setCatalogue(data);
      } catch {
        if (request.live) setCatalogue(null);
      } finally {
        if (request.live) setLoading(false);
      }
    })();

    // A menu closed and reopened while the first fetch is still out must not be
    // overwritten by that stale response.
    return () => {
      request.live = false;
    };
  }, [roomId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const providers = useMemo(() => availableProviders(catalogue), [catalogue]);
  const assets = useMemo(() => cryptoAssets(catalogue), [catalogue]);

  const go = useCallback(
    (
      packageKey: string,
      options?: { provider?: GiftSubProvider; asset?: string },
    ) => {
      // One checkout at a time. Two taps would otherwise open two sessions, and
      // somebody who pays both has been charged twice for one gift.
      if (pending) return;

      setPending(options?.asset ?? options?.provider ?? packageKey);
      setError(null);

      void startGiftSubCheckout(roomId, packageKey, options)
        .then((data) => {
          // Leaving the site is the success case: the charge happens on the
          // processor's page, never here.
          window.location.href = data.checkout_url;
          return undefined;
        })
        .catch((err: unknown) => {
          setError(errorMessageOf(err));
          setPending(null);
        });
    },
    [pending, roomId],
  );

  const handleBuy = useCallback(
    (pkg: GiftSubPackage) => {
      setChosen(pkg);
      setError(null);

      // Nothing to choose between: go straight to the only rail there is,
      // rather than showing a chooser with one row in it.
      if (!needsProviderChoice(catalogue)) {
        if (providers[0] === 'privcart') {
          setStep('asset');
        } else {
          go(pkg.key);
        }
        return;
      }

      setStep('method');
    },
    [catalogue, providers, go],
  );

  const handleMethod = useCallback(
    (provider: GiftSubProvider) => {
      if (!chosen) return;

      if (provider === 'privcart') {
        setError(null);
        setStep('asset');
        return;
      }

      go(chosen.key, { provider: 'stripe' });
    },
    [chosen, go],
  );

  const back = useCallback(() => {
    if (pending) return;
    setError(null);
    setStep(
      step === 'asset' && needsProviderChoice(catalogue)
        ? 'method'
        : 'packages',
    );
  }, [pending, step, catalogue]);

  const usable = isUsableCatalogue(catalogue);
  const open = usable && catalogue.enabled;
  const price = chosen
    ? formatPrice(chosen.amount_cents, catalogue?.currency)
    : '';

  return createPortal(
    <div
      className='gift-subs__overlay'
      onClick={onClose}
      data-testid='gift-subs-overlay'
    >
      <div
        className='gift-subs'
        role='dialog'
        aria-label='Gift Subs'
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className='gift-subs__header'>
          {step !== 'packages' && (
            <button
              type='button'
              className='gift-subs__back'
              onClick={back}
              aria-label='Back'
              data-testid='gift-subs-back'
            >
              <Icon id='arrow_back' icon={ArrowBackIcon} />
            </button>
          )}
          <h3 className='gift-subs__title'>
            {step === 'packages'
              ? `Gift Subs to ${hostName}`
              : 'How would you like to pay?'}
          </h3>
          <button
            type='button'
            className='gift-subs__close'
            onClick={onClose}
            aria-label='Close'
          >
            <Icon id='close' icon={CloseIcon} />
          </button>
        </div>

        {loading && <div className='gift-subs__empty'>Loading…</div>}

        {!loading && !usable && (
          <div className='gift-subs__empty' role='alert'>
            Gift Subs are not available right now.
          </div>
        )}

        {!loading && usable && !catalogue.enabled && (
          <div className='gift-subs__empty' data-testid='gift-subs-closed'>
            Gifting is not open on this instance yet.
          </div>
        )}

        {open && step === 'packages' && (
          <>
            <div className='gift-subs__list'>
              {catalogue.packages.map((pkg) => (
                <div className='gift-subs__row' key={pkg.key}>
                  <span className='gift-subs__icon' aria-hidden='true'>
                    <Icon id='card_giftcard' icon={CardGiftcardIcon} />
                  </span>
                  <span className='gift-subs__label'>
                    {packageLabel(pkg.quantity)}
                  </span>
                  <button
                    type='button'
                    className='gift-subs__buy'
                    disabled={pending !== null}
                    data-testid={`gift-subs-buy-${pkg.key}`}
                    onClick={() => {
                      handleBuy(pkg);
                    }}
                    aria-label={`${packageLabel(pkg.quantity)} for ${formatPrice(
                      pkg.amount_cents,
                      catalogue.currency,
                    )}`}
                  >
                    {pending === pkg.key
                      ? 'Opening…'
                      : `Pay ${formatPrice(pkg.amount_cents, catalogue.currency)}`}
                  </button>
                </div>
              ))}
            </div>

            {/* What the buyer actually gets, stated plainly. The ticket leaves
                what a sub GIVES its recipient still to be decided, so this
                promises only what is true today: the money and the credit. */}
            <p className='gift-subs__note'>
              Your gift goes to {hostName} and is announced in this room. 70%
              goes to the host, 10% to the room&apos;s co-hosts, and 20% to
              nofilter.social.
            </p>
          </>
        )}

        {open && step === 'method' && chosen && (
          <>
            <p className='gift-subs__summary' data-testid='gift-subs-summary'>
              {packageLabel(chosen.quantity)} — <strong>{price}</strong>
            </p>

            <div className='gift-subs__methods'>
              {providers.map((provider) => (
                <button
                  type='button'
                  className='gift-subs__method'
                  key={provider}
                  disabled={pending !== null}
                  data-testid={`gift-subs-method-${provider}`}
                  onClick={() => {
                    handleMethod(provider);
                  }}
                >
                  {/* A CSS accent rather than an icon: this repo ships only
                      the icon assets it actually uses, and inventing path data
                      for a card or a coin glyph is a worse bet than colour. */}
                  <span
                    className={`gift-subs__method-accent gift-subs__method-accent--${provider}`}
                    aria-hidden='true'
                  />
                  <span className='gift-subs__method-body'>
                    <span className='gift-subs__method-label'>
                      {pending === provider
                        ? 'Opening…'
                        : PROVIDER_LABELS[provider]}
                    </span>
                    <span className='gift-subs__method-hint'>
                      {PROVIDER_HINTS[provider]}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {open && step === 'asset' && chosen && (
          <>
            <p className='gift-subs__summary' data-testid='gift-subs-summary'>
              {packageLabel(chosen.quantity)} — <strong>{price}</strong>
            </p>

            {assets.length === 0 ? (
              <div className='gift-subs__empty' role='alert'>
                No crypto assets are available right now.
              </div>
            ) : (
              <>
                <p className='gift-subs__note gift-subs__note--tight'>
                  Choose a coin. We generate an invoice for {price} and hand you
                  to PrivCart&apos;s checkout, with a QR code, an address and a
                  live status.
                </p>

                <div className='gift-subs__assets'>
                  {assets.map((asset, index) => (
                    <button
                      type='button'
                      className={
                        index === 0
                          ? 'gift-subs__asset gift-subs__asset--default'
                          : 'gift-subs__asset'
                      }
                      key={asset}
                      disabled={pending !== null}
                      data-testid={`gift-subs-asset-${asset}`}
                      onClick={() => {
                        go(chosen.key, { provider: 'privcart', asset });
                      }}
                    >
                      <span className='gift-subs__asset-name'>
                        {pending === asset ? 'Opening…' : assetLabel(asset)}
                      </span>
                      {/* The server decides which coin leads, and the first one
                          is the default. Marked rather than pre-selected
                          because choosing a coin here IS the purchase. */}
                      {index === 0 && pending !== asset && (
                        <span className='gift-subs__asset-badge'>Default</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* What the buyer actually needs to know at this step: the coin
                    amount is worked out from the dollar price when the invoice
                    is created, so the figure on the next page is the one to
                    send. */}
                <p className='gift-subs__fineprint'>
                  You are gifting {price}. The exact amount to send is
                  calculated at today&apos;s rate and shown on the payment page.
                </p>
              </>
            )}
          </>
        )}

        {error && (
          <div className='gift-subs__error' role='alert'>
            {error}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
